import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState, useEffect } from "react";
import { Box, Text, Image, Button, Icon, Flex, IconButton, } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { ArrowForwardIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import ImageSlider from "../pub/ImageSlider";
import EventsMarquee from "./EventsMarquee";
const swipeUp = keyframes `
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;
const swipeDown = keyframes `
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;
const DEFAULT_COLORS = {
    primary: "#8155FE",
    text: "white",
    bg: "#111111",
    card_bg: "#16171C",
    card_border: "#2B2B2B",
};
export default function MobileVenueHero({ pubDetail, venueName, onOpen, eventsData, pubId, pubColors, appContext, }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const navigate = useNavigate();
    const colors = {
        primary: (_a = pubColors === null || pubColors === void 0 ? void 0 : pubColors.primary) !== null && _a !== void 0 ? _a : DEFAULT_COLORS.primary,
        text: (_b = pubColors === null || pubColors === void 0 ? void 0 : pubColors.text) !== null && _b !== void 0 ? _b : DEFAULT_COLORS.text,
        bg: (_c = pubColors === null || pubColors === void 0 ? void 0 : pubColors.bg) !== null && _c !== void 0 ? _c : DEFAULT_COLORS.bg,
        card_bg: (_d = pubColors === null || pubColors === void 0 ? void 0 : pubColors.card_bg) !== null && _d !== void 0 ? _d : DEFAULT_COLORS.card_bg,
        card_border: (_e = pubColors === null || pubColors === void 0 ? void 0 : pubColors.card_border) !== null && _e !== void 0 ? _e : DEFAULT_COLORS.card_border,
    };
    const videoContainerRef = useRef(null);
    const videoRef = useRef(null);
    const previousTextAtCenterRef = useRef(false);
    const [textAtCenter, setTextAtCenter] = useState(false);
    const [isAnimatingIn, setIsAnimatingIn] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const goBack = () => {
        if (!window.history || window.history.length <= 1) {
            navigate("/home");
        }
        else {
            navigate(-1);
        }
    };
    const toggleVideoMute = () => {
        if (videoRef.current) {
            const newMutedState = !isMuted;
            videoRef.current.muted = newMutedState;
            setIsMuted(newMutedState);
        }
    };
    // Intersection Observer to detect when video container visibility
    useEffect(() => {
        if (!videoContainerRef.current)
            return;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const isLessThan90PercentVisible = entry.intersectionRatio < 0.8;
                setTextAtCenter(isLessThan90PercentVisible);
            });
        }, {
            root: null,
            rootMargin: "0px",
            threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
        });
        observer.observe(videoContainerRef.current);
        return () => {
            observer.disconnect();
        };
    }, []);
    useEffect(() => {
        if (textAtCenter && !previousTextAtCenterRef.current) {
            setIsAnimatingIn(true);
            const timer = setTimeout(() => {
                setIsAnimatingIn(false);
            }, 600);
            return () => clearTimeout(timer);
        }
        else if (!textAtCenter && previousTextAtCenterRef.current) {
            setIsAnimatingIn(false);
        }
        previousTextAtCenterRef.current = textAtCenter;
    }, [textAtCenter]);
    // Force muted + play on load so iOS Safari doesn't show native play overlay
    useEffect(() => {
        var _a;
        const video = videoRef.current;
        if (!video || !((_a = pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.metaData) === null || _a === void 0 ? void 0 : _a.video))
            return;
        video.muted = true;
        const playPromise = video.play();
        if (playPromise === null || playPromise === void 0 ? void 0 : playPromise.catch) {
            playPromise.catch(() => { });
        }
    }, [(_f = pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.metaData) === null || _f === void 0 ? void 0 : _f.video]);
    return (_jsxs(_Fragment, { children: [_jsxs(Flex, { w: "100%", bg: colors.bg, backdropFilter: "blur(8px)", alignItems: "center", justifyContent: "space-between", px: 4, py: 3, children: [appContext === "experience" && (pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.logo) ? (_jsx(Image, { src: pubDetail.logo, alt: "", h: "40px", maxW: "100px", objectFit: "cover", borderRadius: "8px" })) : (_jsx(IconButton, { "aria-label": "Go back", icon: _jsx(ChevronLeftIcon, { boxSize: 6 }), onClick: goBack, bg: colors.card_bg, border: `1px solid ${colors.card_border}`, color: colors.text, borderRadius: "10", size: "md", _hover: { bg: colors.primary }, _active: { bg: colors.primary } })), _jsx(Box, { bg: `linear-gradient(90deg, ${colors.text} 0%, ${colors.card_bg} 100%)`, p: "2px", borderRadius: "md", _hover: {
                            transform: "scale(1.05)",
                        }, transition: "all 0.3s", boxShadow: `0px 0px 10px 0px ${colors.primary}`, children: _jsx(Button, { bg: colors.card_bg, color: colors.text, border: "none", borderRadius: "md", size: "md", px: 4, py: 2, fontSize: "sm", fontWeight: 400, _hover: {
                                bg: colors.primary,
                            }, transition: "all 0.3s", onClick: onOpen, leftIcon: _jsx(Icon, { as: ArrowForwardIcon, boxSize: 5, transform: "rotate(-45deg)" }), w: "100%", h: "100%", children: "Get Reservation" }) })] }), _jsx(EventsMarquee, { events: (eventsData === null || eventsData === void 0 ? void 0 : eventsData.data) || [], pubId: pubId, pubColors: pubColors }), _jsxs(Box, { w: "100%", pb: "50px", position: "relative", children: [_jsxs(Box, { ref: videoContainerRef, position: "relative", w: "100%", h: `calc(100vh - ${((_g = eventsData === null || eventsData === void 0 ? void 0 : eventsData.data) === null || _g === void 0 ? void 0 : _g.length) > 2 ? 120 : 59}px)`, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, sx: {
                            contain: "layout style",
                        }, children: [_jsx(Box, { w: "100%", h: "100%", children: ((_h = pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.metaData) === null || _h === void 0 ? void 0 : _h.video) ? (_jsx("video", { ref: videoRef, src: pubDetail.metaData.video, autoPlay: true, muted: isMuted, loop: true, playsInline: true, preload: "auto", onClick: toggleVideoMute, style: {
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        borderRadius: "0px",
                                        cursor: "pointer",
                                    } })) : (_jsx(ImageSlider, { images: pubDetail.pub_images || [], borderRadius: "0px" })) }), ((_j = pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.metaData) === null || _j === void 0 ? void 0 : _j.video) && (_jsx(IconButton, { "aria-label": isMuted ? "Unmute video" : "Mute video", icon: isMuted ? (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M16.5 12C16.5 10.23 15.48 8.71 14 7.97V10.18L16.45 12.63C16.48 12.43 16.5 12.22 16.5 12ZM19 12C19 12.94 18.8 13.82 18.46 14.64L19.97 16.15C20.62 14.91 21 13.5 21 12C21 7.72 18.01 4.14 14 3.23V5.29C16.89 6.15 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.53C15.58 18.04 14.83 18.46 14 18.7V20.77C15.38 20.45 16.63 19.82 17.68 18.96L19.73 21L21 19.73L12 10.73L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z", fill: "rgba(255, 255, 255, 0.8)" }) })) : (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M3 9V15H7L12 20V4L7 9H3ZM16.5 12C16.5 10.23 15.48 8.71 14 7.97V16.03C15.48 15.29 16.5 13.77 16.5 12ZM14 3.23V5.29C16.89 6.15 19 8.83 19 12C19 15.17 16.89 17.85 14 18.71V20.77C18.01 19.86 21 16.28 21 12C21 7.72 18.01 4.14 14 3.23Z", fill: "rgba(255, 255, 255, 0.8)" }) })), onClick: (e) => {
                                    e.stopPropagation();
                                    toggleVideoMute();
                                }, position: "absolute", bottom: 20, right: 6, zIndex: 15, bg: "rgba(0, 0, 0, 0.4)", backdropFilter: "blur(8px)", color: "white", borderRadius: "full", size: "sm", _hover: {
                                    bg: "rgba(0, 0, 0, 0.6)",
                                }, _active: {
                                    bg: "rgba(0, 0, 0, 0.7)",
                                }, transition: "all 0.2s", sx: {
                                    backfaceVisibility: "hidden",
                                    WebkitBackfaceVisibility: "hidden",
                                } })), _jsxs(Box, { position: "absolute", bottom: "10%", left: "50%", transform: "translate3d(-50%, 0, 0)", textAlign: "center", zIndex: 10, px: 4, w: "100%", sx: {
                                    backfaceVisibility: "hidden",
                                    WebkitBackfaceVisibility: "hidden",
                                }, children: [_jsxs(Text, { fontSize: "2xl", fontWeight: 600, color: "white", textTransform: "uppercase", letterSpacing: "0.1em", mb: 4, textShadow: "2px 2px 8px rgba(0,0,0,0.8)", lineHeight: "1.5", children: ["WELCOME TO", _jsx("br", {}), (venueName === null || venueName === void 0 ? void 0 : venueName.toUpperCase()) || ""] }), (pubDetail === null || pubDetail === void 0 ? void 0 : pubDetail.logo) && (_jsx(Image, { src: pubDetail.logo, alt: venueName || "Venue logo", maxW: "200px", maxH: "100px", mx: "auto", objectFit: "contain", mt: 4, borderRadius: "8px" }))] })] }), _jsx(Box, { position: "fixed", bottom: -1, left: 0, right: 0, w: "100%", bg: colors.card_bg, border: `1px solid ${colors.primary}`, zIndex: 998, px: 4, py: 4, boxShadow: `0px -4px 20px ${colors.primary}33`, borderRadius: "10px 10px 0 0", pointerEvents: textAtCenter ? "auto" : "none", transform: textAtCenter ? "translate3d(0, 0, 0)" : "translate3d(0, 100%, 0)", opacity: textAtCenter ? 1 : 0, sx: {
                            backfaceVisibility: "hidden",
                            WebkitBackfaceVisibility: "hidden",
                            willChange: "transform, opacity",
                            ...(isAnimatingIn && textAtCenter
                                ? {
                                    animation: `${swipeUp} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`,
                                }
                                : {
                                    transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out",
                                }),
                        }, children: _jsxs(Flex, { alignItems: "center", justifyContent: "space-between", gap: 4, w: "100%", children: [_jsx(Box, { flex: 1, children: _jsx(Text, { fontSize: "xs", fontWeight: 400, color: colors.text, lineHeight: "1.5", textAlign: "left", maxW: "150px", children: "Explore & Book Tables or Event Passes" }) }), _jsx(Button, { bg: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary} 100%)`, color: "white", borderRadius: "10", px: 5, py: 3, fontSize: "xs", fontWeight: 400, _hover: {
                                        bg: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary} 100%)`,
                                    }, _active: {
                                        transform: "scale(0.98)",
                                    }, transition: "all 0.3s ease", onClick: onOpen, leftIcon: _jsx(Icon, { as: ArrowForwardIcon, boxSize: 4, transform: "rotate(-45deg)" }), boxShadow: `0px 0px 10px 0px ${colors.primary}`, children: "Get Reservation Now" })] }) })] })] }));
}
